import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, LogIn, ExternalLink, CheckCircle2, Loader2 } from 'lucide-react';

const DEMO_CLIENT_ID = 'ldc_demo_public_client';

const SSOExamplePage = () => {
  const [ready, setReady] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const script = document.createElement('script');
    script.src = '/sdk/lostdc-auth.js';
    script.onload = async () => {
      window.LostDCAuth.init({
        authBase: window.location.origin,
        clientId: DEMO_CLIENT_ID,
        redirectUri: window.location.origin + '/sso/demo',
        scope: 'profile email openid',
      });
      setReady(true);
      try {
        const res = await window.LostDCAuth.handleRedirectCallback();
        if (res) setResult(res);
      } catch (e) {
        setError(e.message);
      }
    };
    script.onerror = () => setError('Failed to load the SDK.');
    document.body.appendChild(script);
  }, []);

  const loginPopup = async () => {
    setError(''); setBusy(true);
    try {
      const res = await window.LostDCAuth.login({ mode: 'popup' });
      setResult(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const loginRedirect = () => {
    setError('');
    window.LostDCAuth.login({ mode: 'redirect' });
  };

  const logout = () => {
    window.LostDCAuth.logout();
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(82_42%_60%/0.06),transparent_55%)] pointer-events-none" />
      <div className="max-w-2xl mx-auto px-4 py-16 relative z-10">
        <div className="mb-10">
          <Badge variant="secondary" className="mb-3">Live Demo · Third-party site</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            Login with <span className="text-pista">LostDrkC</span>
          </h1>
          <p className="text-muted-foreground mt-3">
            This page simulates an external website using the <code className="font-mono text-pista">lostdc-auth.js</code> SDK.
            No client secret is used — it runs on OAuth 2.1 + PKCE, safe for the browser.
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="demo-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!result ? (
          <Card className="border-border/60 bg-card/95" data-testid="demo-login-card">
            <CardHeader>
              <CardTitle>Try it</CardTitle>
              <CardDescription>Pick a flow. A one-click “Continue as …” prompt appears if you already have a session.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full" onClick={loginPopup} disabled={!ready || busy} data-testid="demo-popup-button">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
                Login with LostDrkC (Popup)
              </Button>
              <Button variant="outline" className="w-full" onClick={loginRedirect} disabled={!ready} data-testid="demo-redirect-button">
                <ExternalLink className="h-4 w-4 mr-2" />
                Login with LostDrkC (Redirect)
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-pista/40 bg-card/95" data-testid="demo-success-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-pista" /> Signed in
              </CardTitle>
              <CardDescription>The SDK returned this user via /oauth/userinfo.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={result.user.picture} />
                  <AvatarFallback className="bg-pista/20 text-pista text-lg">{result.user.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-lg font-semibold">{result.user.name}</p>
                  <p className="text-sm text-muted-foreground">{result.user.email}</p>
                  <p className="text-xs font-mono text-muted-foreground mt-1">{result.user.sub}</p>
                </div>
              </div>
              <pre className="text-xs bg-secondary p-3 rounded-lg overflow-x-auto" data-testid="demo-user-json">
{JSON.stringify(result.user, null, 2)}
              </pre>
              <Button variant="outline" onClick={logout} data-testid="demo-logout-button">Reset demo</Button>
            </CardContent>
          </Card>
        )}

        <div className="mt-10 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-2">Add this to your own site in 3 lines:</p>
          <pre className="text-xs bg-secondary p-4 rounded-lg overflow-x-auto">
{`<script src="${window.location.origin}/sdk/lostdc-auth.js"></script>
<script>
  LostDCAuth.init({ authBase: '${window.location.origin}', clientId: 'YOUR_CLIENT_ID', redirectUri: location.origin + '/callback' });
  btn.onclick = async () => { const { user } = await LostDCAuth.login({ mode: 'popup' }); };
</script>`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default SSOExamplePage;
